import * as React from "react";

import styles from "./FormField.module.css";

export interface FormFieldProps {
  label: string;
  /** grid-column span within a 12-col grid; e.g. "span 4" */
  span?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  maxLength?: number;
  /** rendered inside the input's relative wrapper (e.g. a trailing action button) */
  trailingSlot?: React.ReactNode;
  /** replaces the default <input> with a custom control (e.g. MultiSelect) */
  children?: React.ReactNode;
  className?: string;
  /**
   * Renders a static read-only box instead of an <input> — for fields that are
   * never editable (not "disabled while editing", but structurally read-only).
   * Styled as plain text, not a greyed-out input.
   */
  readOnly?: boolean;
  /** Content shown in the read-only box; falls back to `value`. Use for ReactNode content (links, badges) or a custom empty placeholder. */
  valueText?: React.ReactNode;
  /**
   * Контент под контролом, видимый в обоих режимах. `children` в режиме
   * readOnly не рендерятся, поэтому доп. блок (детали, подсказка) кладётся сюда.
   */
  belowSlot?: React.ReactNode;
  /**
   * Тег обёртки. По умолчанию `label` (клик по подписи фокусирует input).
   * Для оверлейных контролов (PrimeReact Dropdown/MultiSelect/TreeSelect)
   * ставь `div`: внутри `<label>` браузер форвардит клик на вложенный input,
   * из-за чего оверлей открывается и тут же закрывается (двойной toggle).
   */
  as?: "label" | "div";
}

/** Labelled input cell in a 12-column form grid, styled per the Apple mockup. */
export const FormField: React.FC<FormFieldProps> = ({
  label, span, value, onChange, placeholder, disabled, invalid, maxLength, trailingSlot, children, className,
  readOnly, valueText, belowSlot, as = "label",
}) => {
  const Tag = as;
  return (
    <Tag
      className={`${styles.field}${className ? ` ${className}` : ``}`}
      style={span ? { gridColumn: span } : undefined}
    >
      <span className={styles.label}>{label}</span>
      <span className={styles.control}>
        {readOnly ? (
          <span className={styles.readOnlyValue}>
            {valueText ?? value ?? <span className={styles.readOnlyPlaceholder}>{placeholder ?? "—"}</span>}
          </span>
        ) : children ?? (
          <input
            className={`${styles.input}${invalid ? ` ${styles.invalid}` : ``}`}
            value={value ?? ""}
            disabled={disabled}
            placeholder={placeholder}
            maxLength={maxLength}
            onChange={(e) => onChange?.(e.target.value)}
          />
        )}
        {trailingSlot}
      </span>
      {belowSlot}
    </Tag>
  );
};
